import { useEffect, useState } from 'react'
import { Link, useHistory } from 'react-router-dom'
import { useSelector } from 'statux'

import Badge from '../../../../components/Badge/Badge'
import Details from '../../../../components/Details/Details'
import Image from '../../../../components/Image/Image'
import Table from '../../../../components/Table/Table'

import { getCssVar } from '../../../../utils/css'
import { graphqlClient, gql } from '../../../../utils/gql'
import { getInitials } from '../../../../utils/strings'

import authProvider from '../../../../utils/auth-provider'

export default function DashboardHomeEbpcJudge (props = {
    className: null
}) {
    const userId = (authProvider.getIdentity() || {}).id

    const isEbpcJudge = authProvider.getDefaultRole() === 'ebpc_judge'

    if (!isEbpcJudge)
        return null

    const history = useHistory()

    const {
        ebpcContest,
        ebpcAssignmentsOpen,
        ebpcAssignmentsEndDateStr,
        ebpcFinalsOpen
    } = useSelector()

    const [assignments, setAssignments] = useState(
        ebpcAssignmentsOpen
                || ebpcFinalsOpen
            ? (history.location.state || {}).ebpcAssignments
                || null
            : [])

    useEffect(async () => {
        if (!userId
                || (!ebpcAssignmentsOpen
                    && !ebpcFinalsOpen))
            return !ebpcAssignmentsOpen
                && !ebpcFinalsOpen
                && setAssignments([])

        const query = await graphqlClient
            .request(gql`
                query {
                    ebpc_assignments (
                        where: {
                            ebpc_judge_id: {
                                _eq: "${userId}"
                            },
                            ebpc_application: {
                                ebpc_contest_id: {
                                    _eq: ${ebpcContest.id}
                                },
                                submitted_at: {
                                    _is_null: false
                                }
                            }
                            ebpc_finals: {
                                _eq: ${ebpcFinalsOpen}
                            }
                        },
                        order_by: {
                            ebpc_application_id: asc
                        }
                    ) {
                        invest
                        rating
                        recused_at
                        submitted_at
                        ebpc_application {
                            id
                            data
                            venture: data(path: "aboutYourVenture.ventureName")
                            submitted_at
                            profile {
                                company
                                user {
                                    id
                                    avatar_url
                                    display_name
                                }
                            }
                        }
                        ebpc_ratings (where: {
                            ${ebpcFinalsOpen
                                ? gql`ebpc_finals: { _eq: true }`
                                : ''}
                        }) {
                            type
                            rating
                        }
                    }

                    ebpc_ratings_types_aggregate {
                        aggregate {
                            count
                        }
                    }
                }
            `)
            .then(obj => obj.ebpc_assignments
                .map((_obj) => {
                    const ratingsTypesCount = obj.ebpc_ratings_types_aggregate
                        .aggregate
                        .count || 0

                    _obj.status = 'Not started'
                    _obj.statusType = null

                    if (_obj.ebpc_ratings.length
                            || _obj.rating
                            || _obj.invest !== null) {
                        _obj.status = 'In progress'
                        _obj.statusType = 'warning'
                    }

                    if (ratingsTypesCount === _obj.ebpc_ratings.length
                            && _obj.rating
                            && _obj.invest !== null) {
                        _obj.status = 'Completed'
                        _obj.statusType = 'success'
                    }

                    if (_obj.recused_at) {
                        _obj.status = 'Recused'
                        _obj.statusType = 'danger'
                    }

                    _obj.plan = `24-${`${_obj.ebpc_application.id}`
                        .padStart(4, '0')}`

                    _obj.venture = _obj.ebpc_application.venture
                        || _obj.ebpc_application.profile.company

                    _obj.applicant = _obj.ebpc_application.profile.user

                    return _obj
                }))
            .catch((err) => {
                console.warn(err)
                return []
            })

        if (JSON.stringify(assignments)
                === JSON.stringify(query))
            return

        setAssignments(query)

        history.replace(
            `${history.location.pathname}${history.location.search}`,
            Object.assign(history.location.state || {}, {
                ebpcAssignments: query
            }))
    }, [])

    if (assignments === null)
        return null

    const isAssignments = Boolean(assignments.length)

    const isSubmitted = isAssignments
        && assignments
            .filter(obj => !obj.recused_at)
            .length
                === assignments
                    .filter(obj => !obj.recused_at
                        && obj.submitted_at)
                    .length

    let assignmentsHeading = ''

    let assignmentsText = ''

    if (isAssignments) {
        assignmentsHeading = isSubmitted
            ? 'You\'ve completed and submitted your reviews'
            : 'Applications for your review'

        if (isSubmitted)
            assignmentsText = 'Thank you for your support!'
    }

    if (!isAssignments) {
        assignmentsHeading = ebpcAssignmentsOpen
            ? 'Application assignments coming soon'
            : 'The competition is not open at this time'

        assignmentsText = ebpcAssignmentsOpen
            ? 'We\'re working to complete the application period, then validate and assign applications to judges. We\'ll let you know when we\'re ready for you to start reviewing applications. Thank you for your patience.'
            : 'Thank you for checking in; we\'ll be in touch.'
    }

    return (
        <>
            {(isAssignments
                || (assignmentsHeading
                    && assignmentsText))
                && (
                    <section
                        className={`dashboard-home-ebpc-judge-assignments card card-offscreen-x-lg-down margin-1-b margin-3-b-lg-up${
                            ebpcFinalsOpen
                                ? ' dashboard-home-ebpc-judge-assignments-finals'
                                : ''}`}
                    >
                        <div className="row flex-justify-content-space-between">
                            <div
                                className={`col${isAssignments
                                    ? ' col-12-md-down pad-4-r-md-up'
                                    : ''}`}
                            >
                                {assignmentsHeading
                                    && <h2 className="heading-5">{assignmentsHeading}</h2>}

                                {assignmentsText
                                    && <p className="body-4">{assignmentsText}</p>}
                                {isAssignments
                                    && (
                                        <div className="rich-text body-4">
                                            {!isSubmitted
                                                && !ebpcFinalsOpen
                                                && (
                                                    <>
                                                        <p>
                                                            Before starting, please explore
                                                            our <Link to="/dashboard/ebpc/judge-orientation">Judge orientation page</Link> to
                                                            learn about the process.
                                                        </p>
                                                        <p>
                                                            Once ready, please review and rate each application below,
                                                            then make sure to rank them before submitting your final ratings.
                                                            {!ebpcFinalsOpen
                                                                && ebpcAssignmentsEndDateStr
                                                                && ` Please complete all reviews by ${ebpcAssignmentsEndDateStr}.`}
                                                        </p>
                                                        <p>
                                                            If necessary, you may recuse yourself from judging individual applications.
                                                        </p>
                                                    </>)}
                                            {ebpcFinalsOpen
                                                && (
                                                    <p>
                                                        Below, you'll find links to each of our finalists' applications.
                                                        Please review at your convenience.
                                                        We're excited to have you judging the Finals!
                                                    </p>)}
                                        </div>)}
                            </div>
                            {isAssignments
                                && (
                                    <>
                                        {!isSubmitted
                                            && (
                                                <div className="col-auto margin-t-md-down">
                                                    <Link
                                                        to={`/dashboard/ebpc/applications/${
                                                            ((assignments
                                                                .filter(obj => !obj.recused_at)[0] || {})
                                                                .applicant || {})
                                                                .id}`}
                                                        className="btn btn-secondary"
                                                    >
                                                        Review applications
                                                    </Link>
                                                </div>)}
                                        <div className="dashboard-ebpc-applications col-12 margin-3-t">
                                            <Table
                                                style={{
                                                    color: getCssVar('--navy')
                                                }}
                                            >
                                                <thead>
                                                    <tr>
                                                        <th>
                                                            <span>Plan</span>
                                                            <span className="display-none-sm-down"> number</span>
                                                            <span className="display-none-sm-up" aria-label="number"> #</span>
                                                        </th>
                                                        <th>Venture name</th>
                                                        <th>Applicant name</th>
                                                        {!ebpcFinalsOpen
                                                            && (
                                                                <th>
                                                                    <span
                                                                        style={{
                                                                            paddingLeft: 15
                                                                        }}
                                                                    >
                                                                        Status
                                                                    </span>
                                                                </th>)}
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {assignments
                                                        .map((obj, idx) => (
                                                            <tr
                                                                key={`dashboard-home-ebpc-judge-assignments-${idx}`}
                                                            >
                                                                <td><span>{obj.plan}</span></td>
                                                                <td
                                                                    className="table-col-venture"
                                                                >
                                                                    <Link
                                                                        to={`/dashboard/ebpc/applications/${obj.applicant.id}`}
                                                                        title="View application"
                                                                    >
                                                                        <span>{obj.venture}</span>
                                                                    </Link>
                                                                </td>
                                                                <td
                                                                    className="table-col-applicant"
                                                                >
                                                                    <Link
                                                                        to={`/dashboard/profiles/${obj.applicant.id}`}
                                                                        title={`View profile of ${obj.applicant.display_name}`}
                                                                        aria-label={obj.applicant.display_name}
                                                                    >
                                                                        <span className="avatar avatar-sm">
                                                                            <Image
                                                                                src={!(obj.applicant.avatar_url || '')
                                                                                    .match(/gravatar\.com/)
                                                                                    ? obj.applicant.avatar_url
                                                                                    : null}
                                                                                textFallback={getInitials(obj.applicant.display_name)}
                                                                                data-object-fit="cover"
                                                                            />
                                                                        </span>
                                                                        <span>{obj.applicant.display_name}</span>
                                                                    </Link>
                                                                </td>
                                                                {!ebpcFinalsOpen
                                                                    && (
                                                                        <td>
                                                                            <Badge
                                                                                type={obj.statusType}
                                                                            >
                                                                                {obj.status}
                                                                            </Badge>
                                                                        </td>)}
                                                            </tr>
                                                        ))}
                                                </tbody>
                                            </Table>
                                        </div>
                                    </>)}
                        </div>
                    </section>)}

            {!ebpcFinalsOpen
                && (
                    <section className="dashboard-home-ebpc-judge-faqs card card-offscreen-x-lg-down margin-1-b margin-3-b-lg-up">
                        <h2 className="heading-5">Judges often ask.</h2>

                        <p className="body-4 margin-b">
                            To make sure your judging experience is as enjoyable and
                            easy as possible, we've assembled some of the most common
                            questions we've received from judges.
                        </p>

                        <Details
                            className="card card-border margin-b"
                            summary="What information do applicants provide?"
                            text="EBPC applicants were required to provide comprehensive details on their education venture, covering innovative products, management teams, target markets, marketing strategies, competitive advantages, and financial plans for the first three years. They also explained how their offerings address systemic barriers in education to support underserved communities, with encouragement to include a 60-second video pitch and a supplemental digital slide deck."
                        />

                        <Details
                            className="card card-border margin-b"
                            summary="What is the judging criteria?"
                            text="Applicants for EBPC are evaluated on a scale of 1-10 across multiple criteria, including the clarity and innovation of their product or service, the suitability and expertise of their team, the identification and viability of their target market, competitive advantage, financial soundness, and overall venture viability. Additionally, ventures are assessed for their potential impact on equitable outcomes in education, considering factors such as mission focus, addressing gaps for diverse learners, and spurring positive change. Judges also rank ventures based on overall merit and indicate whether they would fund the business plan.<br><br><a href='/dashboard/ebpc/judge-orientation' style='text-decoration: underline'>Review the Judge orientation page</a>"
                        />

                        <Details
                            className="card card-border"
                            summary="What if I have a conflict with a venture assigned to me?"
                            text='If a judge has a conflict with a venture assigned to them, such as a pre-existing relationship as an investor or advisory/board member, they can use the "Recuse from Judging" button on their assigned submissions page. This action will automatically reassign them to a different application for rating.'
                        />
                    </section>)}
        </>
    )
}
